import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { getCommentThreadByDateRange } from "../../apis/AdminDashboard/users";
import { useSelector } from "react-redux";

/**
 * @description VerificationProjectCard component, used to display the project cards in the verification page. Also used to display comments(pass isComment=true) in the comments section.
 * @param {*} facility
 * @param {*} link
 * @param {*} loading
 * @param {*} isComment
 * @param {*} canVerify
 * @returns
 */
export default function VerificationProjectCard({
  facility,
  link,
  loading,
  isComment = false,
  canVerify = true
}) {
  const [open, setOpen] = useState(true);
  const [openViewCommentModal, setOpenViewCommentModal] = useState(false);
  const [commentThread, setCommentThread] = useState([]);
  const [hasComments, setHasComments] = useState(false);

  const auth = useSelector((state) => state.auth);

  // Check if facility has comments on mount
  useEffect(() => {
    const checkForComments = async () => {
      try {
        const plantAddress = facility?.walletAddress || facility?.plantAddress;
        const rangeStartDate = facility?.rangeStartDate;
        const rangeEndDate = facility?.rangeEndDate;

        if (!plantAddress || !rangeStartDate || !rangeEndDate) return;

        const response = await getCommentThreadByDateRange(plantAddress, rangeStartDate, rangeEndDate, auth?.token);
        const threads = response?.data?.data?.threads || {};

        const allComments = Object.entries(threads).flatMap(([threadId, comments]) =>
          comments.map(c => ({ ...c, threadId }))
        ).sort((a, b) => Number(a.createdAt) - Number(b.createdAt));

        setHasComments(allComments.length > 0);
      } catch (err) {
        console.error("Error checking comments:", err);
      }
    };

    if (facility && auth?.token) {
      checkForComments();
    }
  }, [facility, auth?.token]);

  // Fetch comments when modal opens
  const handleViewComment = async () => {
    try {
      const plantAddress = facility?.walletAddress || facility?.plantAddress;
      const rangeStartDate = facility?.rangeStartDate;
      const rangeEndDate = facility?.rangeEndDate;

      if (!plantAddress || !rangeStartDate || !rangeEndDate) return;

      const response = await getCommentThreadByDateRange(plantAddress, rangeStartDate, rangeEndDate, auth?.token);
      const threads = response?.data?.data?.threads || {};

      const allComments = Object.entries(threads).flatMap(([threadId, comments]) =>
        comments.map(c => ({ ...c, threadId }))
      ).sort((a, b) => Number(a.createdAt) - Number(b.createdAt));

      setCommentThread(allComments);
      setOpenViewCommentModal(true);
    } catch (err) {
      console.error("Error fetching comment thread:", err);
    }
  };

  return (
    <div className="flex flex-col items-start p-10 gap-6 w-[1042px] mb-3 max-w-[1042px] bg-gray-50 rounded-lg">
      {/* Header Section */}
      <div className="flex flex-row items-center justify-between w-full h-6">
        <div className="flex-1 min-w-0">
          <h3 className="text-[16px] font-medium text-gray-900 leading-6 truncate">
            Organizational Name - {facility?.facilityName || "Unknown Facility"}
          </h3>
        </div>
        <div className="flex items-center justify-end gap-3 w-[239px]">
          <div className="text-[14px] font-normal text-gray-900 text-right leading-5">
            {facility?.rangeStartDate && facility?.rangeEndDate
              ? `${facility.rangeStartDate} - ${facility.rangeEndDate}`
              : `${facility.verificationDate}`}
          </div>
          <button
            onClick={() => setOpen((prev) => !prev)}
            className="flex items-center justify-center w-10 h-10 cursor-pointer transform transition-transform"
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transform ${!open ? "rotate-180" : ""} transition-transform`}
            >
              <circle
                cx="12"
                cy="12"
                r="12"
                transform="matrix(1 0 0 -1 0 24)"
                fill="#D0D5DD"
              />
              <path
                d="M8.66683 13.3333L12.0002 10L15.3335 13.3333"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {open && (
        <>
          <div className="w-full border-t border-gray-300 mt-5 mb-5"></div>
          <div className="flex flex-row justify-between items-center w-full h-12 gap-6">
            <div className="flex flex-row items-center gap-6 flex-1">
              <div className="flex flex-col items-center w-full gap-2 flex-1 min-w-0">
                <div className="text-[14px] font-normal leading-relaxed text-gray-900 leading-5 w-full">
                  Total Project emission data:
                </div>
                <div className="text-[14px] font-medium text-gray-500 leading-5 mt-3 w-full">
                  {facility?.totalGHGEmission || "0.00"} tCO2e
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 w-full flex-1 min-w-0">
                <div className="text-[14px] font-normal leading-relaxed text-gray-900 leading-5 w-full">
                  Total Baseline emission data:
                </div>
                <div className="text-[14px] font-medium text-gray-500 leading-5 mt-3 w-full">
                  {facility?.quantifiedGHGEmissionsInBaselineScenario || "0.00"} tCO2e
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                <div className="text-[14px] font-normal leading-relaxed text-gray-900 leading-5 w-full">
                  Total Energy consumption:
                </div>
                <div className="text-[14px] font-medium text-gray-500 leading-5 mt-3 w-full">
                  {facility?.energyConsumptionInKWh || "0.00"} kWh
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 flex-1">
                <div className="text-[14px] font-normal leading-relaxed text-gray-900 leading-5 w-full">
                  Total Emission reduction:
                </div>
                <div className="text-[14px] font-medium text-gray-500 leading-5 mt-3 w-full">
                  {facility?.emissionReductionInKWh || "0.00"} kWh
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                <div className="text-[14px] font-normal text-gray-900 leading-5 w-full">
                  Reporting period:
                </div>
                <div className="text-[14px] font-medium text-gray-500 leading-5 mt-3 w-full capitalize">
                  {facility?.reportingPeriod || "Not specified"}
                </div>
              </div>
            </div>

            {/* View Comment Link - Show if comments exist */}
            {(isComment || hasComments) && (
              <button
                onClick={handleViewComment}
                className="font-semibold text-[14px] text-[#038B47] underline hover:opacity-80 transition-opacity"
              >
                View Comment
              </button>
            )}
{
  canVerify &&(
     <div className="flex items-center gap-3 w-[145px] h-[50px]">
              {loading ? (
                <div className="flex justify-center items-center w-full h-[50px] bg-gray-400 rounded-lg text-white font-semibold">
                  Loading...
                </div>
              ) : (
                <Link
                  to={link}
                  className="flex justify-center items-center gap-3 w-full h-[50px] text-[16px] px-4 bg-gradient-to-r from-[#038B47] to-[#00AED9] rounded-lg text-white font-semibold leading-6 hover:opacity-90 transition-opacity"
                >
                  Verify
                </Link>
              )}
            </div>
  )
}
           
            
          </div>
        </>
      )}

      {/* View Comment Modal */}
      {openViewCommentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setOpenViewCommentModal(false)}
        >
          <div
            className="relative w-[600px] bg-white rounded-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <button
                onClick={() => setOpenViewCommentModal(false)}
                className="flex items-center gap-2 text-[#667085] hover:text-[#101828] transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15.8334 10H4.16675M4.16675 10L10.0001 15.8333M4.16675 10L10.0001 4.16667" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="font-medium text-[14px]">Back</span>
              </button>
              <h2 className="absolute left-1/2 transform -translate-x-1/2 font-medium text-[24px] text-[#101828]">
                View Comment
              </h2>
              <div className="w-[60px]"></div> {/* Spacer for centering */}
            </div>

            {/* Comment Display Box - Only Comments */}
            {commentThread.length === 0 ? (
              <div className="border border-[#E4E7EC] rounded-lg p-6 min-h-[200px] flex items-center justify-center text-[#667085]">
                No comments yet
              </div>
            ) : (
              <div className="border border-[#E4E7EC] rounded-lg p-6 min-h-[200px] bg-white">
                {commentThread.map((comment, index) => (
                  <div key={comment.id || index} className={index > 0 ? "mt-4 pt-4 border-t border-[#E4E7EC]" : ""}>
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-[14px] font-semibold text-[#101828] capitalize">
                        {comment.userRole || 'verifier'}
                      </span>
                      <span className="text-[12px] text-[#667085]">
                        {comment.createdAt ? new Date(Number(comment.createdAt)).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-[14px] text-[#344054] leading-relaxed">
                      {comment.comment}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
 